// WHAT THEY SAID THEY WANTED, READ OUT OF WHATEVER SHAPE THE FORM WAS STORED IN.
//
// A form is filled in once. The answers — what they want ads to do, whether they
// run LinkedIn Ads today, the budget they named — are the most specific thing the
// CRM will ever hold about a lead, and until now they were visible to the drafter
// and to nobody else: the card quoted the free-text message and dropped every
// structured answer on the floor.
//
// THREE STORAGE SHAPES, ONE READER. The same question has been written down three
// different ways over the life of this system, and a card that only understands
// the newest one shows nothing for almost every lead already in the CRM:
//
//   metadata.form_context  the named answers, captured forward from Sep 2026
//                          (goals, budget, runningLinkedinAds, hearAboutUs…)
//   metadata.form          the normalised intake body — every webflow submission
//                          has one, but only message/country/hear_about_us/
//                          referred_by survived the normalisation
//   metadata.values        HubSpot's own [{name, value}] from the import, whose
//                          field names are whatever that form asked
//
// DEGRADES BY DISAPPEARING, like every other panel on the page: a shape we cannot
// read, or one with nothing in it, produces an empty list and the card that calls
// this renders nothing at all. Nothing here invents an answer — a lead whose row
// predates a field simply has fewer lines, which is the honest outcome.
//
// UNKNOWN KEYS STILL RENDER. The named fields below are ordered because they are
// the ones Gabriel reads first; anything else the form asked comes out after them
// under its own name, so adding a question to the website does not quietly
// vanish from the CRM until someone edits this file.

/** Blank in every shape a stored answer can be blank in. */
const blank = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && !v.trim()) ||
  (Array.isArray(v) && !v.filter(Boolean).length);

const text = (v) => (Array.isArray(v) ? v.filter(Boolean).join(" · ") : String(v).trim());

/**
 * Identity and plumbing, never answers. These are the lead THEMSELF (already in
 * the header and the contact card) or bookkeeping about the submission, and
 * repeating them under "from the form" would bury the three lines that matter.
 * `message`/`about` are excluded too: their words are quoted in full by the
 * stage panel, and a truncated second copy here would compete with it.
 */
const NOT_AN_ANSWER = new Set([
  "first_name", "last_name", "name", "firstname", "lastname", "fullname", "full_name",
  "email", "company", "company_name", "phone", "website",
  "message", "about", "notes", "comments", "details",
  "form_source", "form_id", "page_url", "landing_page", "referrer", "utm", "country", "li_fat_id",
]);

const isUtm = (k) => /^utm_|^hs_|^gclid$|^fbclid$/i.test(k);

/**
 * WHAT THE WARM-INTRO TRIAGE WRITES, WHICH IS NOT A FORM.
 *
 * When the Gmail watcher mints a lead from an introduction (Shahar at Pluto,
 * introduced by Dani Avitz, Sep 2026), it records the referral on the contact
 * in the same shape the form intake uses — `self_reported.referred_by` and the
 * HubSpot-era flag `were_you_referred_to_us_by_anyone_: true` — because that is
 * the shape src/leadSource.js reads as authoritative attribution. Attribution,
 * not an answer: nobody filled anything in. An intro lead must read as "no
 * form", so these keys never render here even if a caller hands this reader a
 * shape that carries them, and the "Referred" row only ever comes from what a
 * person typed on the website (`referral` / `referred_by` in the submission).
 */
const INTRO_TRIAGE_KEYS = new Set(["self_reported", "were_you_referred_to_us_by_anyone_"]);

/**
 * Only a form submission holds form answers. A timeline row (`subtype`) or an
 * activity row (`type`) that names itself as anything else — the introduction
 * email on an intro lead's timeline, say — is not read at all; an entry that
 * names nothing is read on its metadata alone, which a contact row has none of.
 */
const isFormEntry = (entry) => {
  if (!entry || typeof entry !== "object") return false;
  const kind = entry.subtype || entry.type;
  return !kind || kind === "form_submission";
};

/**
 * The named answers, in the order the form asks them — which is the order Gabriel
 * already reads them in the notification email.
 *
 * Each entry names every key that has ever held that answer, newest first. The
 * HubSpot import's own field names are in here too (`linkedin_ads_budget`), which
 * is why a migrated 2026 form submission reads the same as one from this morning.
 */
const NAMED = [
  { label: "Goals", keys: ["goals"] },
  { label: "Running ads", keys: ["runningLinkedinAds", "running_linkedin_ads", "Are you currently running Linkedin Ads?"] },
  { label: "Budget", keys: ["budget", "linkedin_ads_budget", "Budget"] },
  { label: "How they heard", keys: ["hearAboutUs", "hear_about_us", "how_did_you_hear", "How did you hear about us?"] },
  { label: "Referred", keys: ["referral", "referred_by", "Were you referred to us by anyone?"] },
];

/** Prettify a raw field name we have no label for: `lead_source` → "Lead source". */
function labelFor(key) {
  const words = String(key).replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** HubSpot's [{name, value}] → a plain object, so one pass reads every shape. */
function fromValues(values) {
  const out = {};
  if (!Array.isArray(values)) return out;
  for (const v of values) {
    if (v && typeof v === "object" && v.name && !blank(v.value)) out[v.name] = v.value;
  }
  return out;
}

/**
 * Every answer a form_submission activity is holding, as `{ key, label, value }`
 * rows ready to render. Newest storage shape wins where two disagree, because
 * `form_context` is the parsed answer and `form` is what survived normalisation.
 */
export function formAnswers(entry) {
  if (!isFormEntry(entry)) return [];
  const meta = entry.metadata || {};
  const merged = {
    ...fromValues(meta.values),
    ...(meta.form && typeof meta.form === "object" ? meta.form : {}),
    ...(meta.form_context && typeof meta.form_context === "object" ? meta.form_context : {}),
  };

  const rows = [];
  const used = new Set();

  for (const { label, keys } of NAMED) {
    for (const k of keys) used.add(k);
    const key = keys.find((k) => !blank(merged[k]));
    if (key) rows.push({ key, label, value: text(merged[key]) });
  }

  // Whatever else the form asked, under its own name. Objects are skipped rather
  // than stringified: a nested blob is a storage detail, not something anybody
  // wrote into a field.
  for (const [k, v] of Object.entries(merged)) {
    if (used.has(k) || NOT_AN_ANSWER.has(k) || INTRO_TRIAGE_KEYS.has(k) || isUtm(k)) continue;
    if (blank(v) || (typeof v === "object" && !Array.isArray(v))) continue;
    rows.push({ key: k, label: labelFor(k), value: text(v) });
  }

  return rows;
}

/** The free text they wrote, which the stage panel quotes in their own words. */
export function formMessage(entry) {
  if (!isFormEntry(entry)) return null;
  const meta = entry.metadata || {};
  const fc = meta.form_context || {};
  const form = meta.form || {};
  const hs = fromValues(meta.values);
  const candidate =
    fc.about || form.message || form.notes || form.comments || form.details || hs.message || hs.about;
  return blank(candidate) ? null : String(candidate).trim();
}

/** The most recent form submission in a timeline, or null. */
export function latestFormSubmission(timeline) {
  return (timeline || []).find((i) => i && i.subtype === "form_submission") || null;
}
